import { apiClient } from "../../api/apiClient.js";

/**
 * P164-R4: a company's OWN documents, over the real
 * /api/backoffice/worker-line/plcs/{organizationId}/documents API.
 *
 * Owner: "บริษัทจัดหางานและ จัดหาแรงงานต้องแนบเอกสารได้". Distinct from the MOU
 * round documents in ../mou/mouCaseApi.js: those belong to one import round,
 * which only a job-side company ever has. These belong to the company, and a
 * บริษัทจัดหาแรงงาน needs them just as much.
 *
 * Every write returns the refreshed LIST, so the panel always renders what the
 * server actually stored rather than an optimistic guess that could disagree.
 */

function documentsPath(organizationId) {
  return `/backoffice/worker-line/plcs/${encodeURIComponent(organizationId)}/documents`;
}

/** Every document filed against this company, newest first. */
export function listOrganizationDocuments(organizationId) {
  if (!organizationId) {
    throw new Error("listOrganizationDocuments requires an organizationId");
  }
  return apiClient.get(documentsPath(organizationId));
}

/**
 * Attaches one document. `documentType` is what the operator calls it and
 * `note` is anything worth saying about it - an expiry date, the issuing office.
 */
export function uploadOrganizationDocument(organizationId, file, { documentType = null, note = null } = {}) {
  if (!organizationId) {
    throw new Error("uploadOrganizationDocument requires an organizationId");
  }
  if (!file) {
    throw new Error("uploadOrganizationDocument requires a file");
  }

  const formData = new FormData();
  formData.append("document", file);
  if (documentType) {
    formData.append("documentType", documentType);
  }
  if (note) {
    formData.append("note", note);
  }

  return apiClient.postForm(documentsPath(organizationId), formData);
}

/** Soft-deletes one document. The file and its audit trail survive on the server. */
export function deleteOrganizationDocument(organizationId, documentId) {
  if (!organizationId || !documentId) {
    throw new Error("deleteOrganizationDocument requires an organizationId and a documentId");
  }
  return apiClient.delete(`${documentsPath(organizationId)}/${encodeURIComponent(documentId)}`);
}

/**
 * One document's real bytes. Read through the authenticated client and never as
 * a bare href - the route carries no session token of its own, so a plain link
 * to it would 401.
 */
export function getOrganizationDocumentBlob(organizationId, documentId) {
  if (!organizationId || !documentId) {
    throw new Error("getOrganizationDocumentBlob requires an organizationId and a documentId");
  }
  return apiClient.getBlob(`${documentsPath(organizationId)}/${encodeURIComponent(documentId)}/file`);
}
